import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown, Star, MapPin, Phone, Mail, Clock, Instagram } from "lucide-react";
import { FadeUp, LineReveal } from "@/components/Reveal";
import { heroImage, gallery, googleReviews, featuredReviews, brandLogos, contactInfo, team } from "@/data/content";

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <div data-testid="home-page">
      <section ref={heroRef} className="relative min-h-screen flex items-end overflow-hidden">
        <motion.div style={{ y: bgY }} className="absolute inset-0 scale-110">
          <img src={heroImage.url} alt={heroImage.description} className="h-full w-full object-cover" />
          <div className="absolute inset-0" style={{ backgroundColor: "rgba(12, 15, 28, 0.82)" }} />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0C0F1C] via-transparent to-transparent" />
        </motion.div>

        <motion.div style={{ opacity: fade }} className="relative z-10 w-full px-6 md:px-12 pb-20 md:pb-28 pt-44">
          <h1 className="font-display uppercase leading-[0.88] tracking-tight text-[19vw] md:text-[10.5rem]">
            <LineReveal text="Every Car" delay={0.15} />
            <LineReveal text="Deserves" delay={0.3} className="text-[#E51B23]" />
            <LineReveal text="Attention" delay={0.45} />
          </h1>
          <div className="mt-12 flex flex-col md:flex-row md:items-center justify-between gap-10">
            <FadeUp delay={0.6}>
              <p className="max-w-md text-zinc-400 text-sm md:text-base leading-relaxed">
                Collision repair &amp; vehicle restoration in Kelowna, BC — done right, the first time.
              </p>
            </FadeUp>
            <FadeUp delay={0.7} className="flex flex-wrap gap-4">
              <Link to="/book" data-testid="hero-book-cta" className="osc-btn-primary">
                Book Appointment <ArrowRight size={15} />
              </Link>
              <Link to="/quote" data-testid="hero-estimate-cta" className="osc-btn-outline">
                Free Estimate
              </Link>
            </FadeUp>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4 }}
          className="absolute bottom-8 right-6 md:right-12 z-10 text-zinc-500"
        >
          <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2 }}>
            <ArrowDown size={20} />
          </motion.div>
        </motion.div>
      </section>

      <section className="px-6 md:px-12 py-16 md:py-20 border-t border-zinc-800" data-testid="manifesto-section">
        <FadeUp className="flex flex-col md:flex-row md:items-center justify-between gap-8">
          <h2 className="font-display uppercase text-3xl sm:text-4xl lg:text-5xl leading-tight max-w-xl">
            Certified journeymen run this shop
          </h2>
          <p className="max-w-md text-zinc-400 text-sm leading-relaxed">
            Our shop floor is staffed by certified journeymen — more than capable
            of bringing your vehicle's vision to life.
          </p>
        </FadeUp>
      </section>

      <section className="border-t border-zinc-800" data-testid="brands-section">
        <FadeUp className="px-6 md:px-12 pt-20 md:pt-24 pb-12 flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <p className="text-xs uppercase tracking-[0.35em] text-[#E51B23] mb-4">Certified &amp; Trusted</p>
            <h2 className="font-display uppercase text-3xl sm:text-4xl lg:text-5xl leading-none">
              Certified to work on the names you drive
            </h2>
          </div>
          <p className="max-w-sm text-zinc-500 text-sm leading-relaxed">
            From daily drivers to insurance claims — we're trusted by the brands
            and insurers Okanagan drivers rely on.
          </p>
        </FadeUp>
        <FadeUp className="px-6 md:px-12 pb-20 md:pb-24 border-b border-zinc-800">
          <div className="flex flex-wrap items-center justify-center gap-x-12 md:gap-x-20 gap-y-10" data-testid="brand-logos">
            {brandLogos.map((b) =>
              b.logo ? (
                <img
                  key={b.name}
                  src={b.logo}
                  alt={`${b.name} logo`}
                  data-testid={`brand-logo-${b.name.toLowerCase().replace(/\s+/g, "-")}`}
                  className="h-9 md:h-12 w-auto opacity-70 hover:opacity-100 transition-opacity duration-300"
                />
              ) : (
                <span
                  key={b.name}
                  data-testid={`brand-logo-${b.name.toLowerCase()}`}
                  className="font-display uppercase text-2xl md:text-3xl tracking-wider text-zinc-100/70 hover:text-zinc-100 transition-colors duration-300 flex items-center gap-2"
                >
                  {b.name}
                  {b.name === "Dodge" && (
                    <span className="flex gap-1">
                      <span className="w-1.5 h-6 bg-[#E51B23] -skew-x-[20deg] inline-block" />
                      <span className="w-1.5 h-6 bg-[#E51B23] -skew-x-[20deg] inline-block" />
                    </span>
                  )}
                </span>
              )
            )}
          </div>
        </FadeUp>
      </section>

      <section className="px-6 md:px-12 py-24 md:py-32 border-t border-zinc-800" data-testid="services-preview">
        <FadeUp className="mb-16">
          <p className="text-xs uppercase tracking-[0.35em] text-[#E51B23] mb-4">The Crew</p>
          <h2 className="font-display uppercase text-4xl sm:text-5xl lg:text-6xl leading-none">
            Meet our team
          </h2>
        </FadeUp>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {team.map((s, i) => (
            <FadeUp key={s.num} delay={i * 0.12}>
              <div data-testid={`team-card-${s.num}`} className="group border border-zinc-800 bg-[#131A2E] hover:border-[#E51B23]/60 transition-colors duration-500">
                <div className="overflow-hidden aspect-[16/9]">
                  <img src={s.image} alt={s.title} className="img-spotlight h-full w-full object-cover" />
                </div>
                <div className="p-8">
                  <span className="font-display text-outline text-4xl">{s.num}</span>
                  <h3 className="font-display uppercase text-2xl mt-3 text-zinc-100 group-hover:text-[#E51B23] transition-colors duration-300">
                    {s.title}
                  </h3>
                  <p className="text-zinc-500 text-sm mt-3 leading-relaxed">{s.blurb}</p>
                </div>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      <section id="work" className="px-6 md:px-12 py-24 md:py-32 border-t border-zinc-800" data-testid="work-section">
        <FadeUp className="mb-16">
          <p className="text-xs uppercase tracking-[0.35em] text-[#E51B23] mb-4">Our Work</p>
          <h2 className="font-display uppercase text-4xl sm:text-5xl lg:text-6xl leading-none">
            The proof is in the panels
          </h2>
        </FadeUp>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          {gallery.map((g, i) => (
            <FadeUp key={g.title} delay={(i % 3) * 0.1}>
              <div className="group" data-testid={`gallery-item-${i}`}>
                <div className="overflow-hidden border border-zinc-800 group-hover:border-[#E51B23]/60 transition-colors duration-500 aspect-[3/4]">
                  <img src={g.image} alt={g.title} className="img-spotlight h-full w-full object-cover" />
                </div>
                <p className="mt-3 text-[10px] uppercase tracking-[0.25em] text-[#E51B23]">{g.tag}</p>
                <p className="font-display uppercase text-sm md:text-base text-zinc-200">{g.title}</p>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      <section className="px-6 md:px-12 py-24 md:py-32 border-t border-zinc-800 bg-[#131A2E]" data-testid="testimonials-section">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <FadeUp className="lg:col-span-7">
            <p className="text-xs uppercase tracking-[0.35em] text-[#E51B23] mb-4">Word Of Mouth</p>
            <h2 className="font-display uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
              Rated by the people<br />who trust us<br />with their cars
            </h2>
            <p className="mt-8 max-w-md text-zinc-400 text-sm md:text-base leading-relaxed">
              Real reviews from real customers on Google — collision repairs, restorations,
              and custom work across the Okanagan.
            </p>
          </FadeUp>

          <FadeUp delay={0.15} className="lg:col-span-5 flex flex-col gap-6">
            <a
              href={googleReviews.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="google-reviews-link"
              className="group block border border-zinc-800 bg-[#0C0F1C] p-10 hover:border-[#E51B23]/60 transition-colors duration-500"
            >
              <div className="flex items-end gap-4">
                <span className="font-display text-8xl leading-none text-zinc-100">{googleReviews.score}</span>
                <div className="pb-2">
                  <div className="flex gap-1" data-testid="google-review-stars">
                    {[0, 1, 2, 3, 4].map((i) => (
                      <Star
                        key={i}
                        size={20}
                        className={i < 4 ? "text-[#E51B23] fill-[#E51B23]" : "text-[#E51B23] fill-[#E51B23]/40"}
                      />
                    ))}
                  </div>
                  <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 mt-3">
                    {googleReviews.count} Google Reviews
                  </p>
                </div>
              </div>
              <p className="mt-8 inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-zinc-300 group-hover:text-[#E51B23] transition-colors duration-300">
                Read them on Google <ArrowRight size={14} />
              </p>
            </a>

            {featuredReviews.map((r, i) => (
              <blockquote key={r.name} className="border border-zinc-800 bg-[#0C0F1C] p-10" data-testid={`review-${i}`}>
                <div className="flex gap-1 mb-5">
                  {Array.from({ length: r.stars }).map((_, s) => (
                    <Star key={s} size={14} className="text-[#E51B23] fill-[#E51B23]" />
                  ))}
                </div>
                <p className="text-zinc-300 text-sm leading-relaxed">"{r.quote}"</p>
                <footer className="mt-6 border-t border-zinc-800 pt-5 flex items-baseline justify-between">
                  <p className="font-display uppercase text-zinc-100">{r.name}</p>
                  <p className="text-[10px] text-zinc-600 uppercase tracking-[0.2em]">{r.source}</p>
                </footer>
              </blockquote>
            ))}
          </FadeUp>
        </div>
      </section>

      <section className="bg-[#E51B23] px-6 md:px-12 py-24 md:py-32" data-testid="cta-section">
        <FadeUp className="flex flex-col md:flex-row md:items-center justify-between gap-10">
          <div>
            <h2 className="font-display uppercase text-4xl sm:text-5xl lg:text-7xl leading-[0.95] text-[#0C0F1C]">
              Your car deserves<br />the best hands<br />in the valley
            </h2>
            <p className="text-[#0C0F1C]/70 mt-6 max-w-md text-sm md:text-base font-medium">
              Book an appointment or get a free estimate — we'll take it from there.
            </p>
          </div>
          <div className="flex flex-col gap-4 shrink-0">
            <Link to="/book" data-testid="cta-book-button" className="inline-flex items-center justify-center gap-2 bg-[#0C0F1C] text-zinc-100 font-semibold uppercase tracking-[0.2em] text-xs px-10 py-5 rounded-sm transition-transform duration-300 hover:scale-[1.03] active:scale-95">
              Book Appointment <ArrowRight size={15} />
            </Link>
            <Link to="/quote" data-testid="cta-quote-button" className="inline-flex items-center justify-center gap-2 border-2 border-[#0C0F1C] text-[#0C0F1C] font-semibold uppercase tracking-[0.2em] text-xs px-10 py-5 rounded-sm transition-all duration-300 hover:bg-[#0C0F1C] hover:text-zinc-100 active:scale-95">
              Free Estimate
            </Link>
          </div>
        </FadeUp>
      </section>

      <section id="contact" className="px-6 md:px-12 py-24 md:py-32 border-t border-zinc-800" data-testid="contact-section">
        <FadeUp className="mb-16">
          <p className="text-xs uppercase tracking-[0.35em] text-[#E51B23] mb-4">Contact</p>
          <h2 className="font-display uppercase text-4xl sm:text-5xl lg:text-6xl leading-none">
            Come by the shop
          </h2>
        </FadeUp>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <FadeUp className="lg:col-span-5">
            <div className="border border-zinc-800 bg-[#131A2E] p-8 md:p-12 space-y-8" data-testid="contact-info-card">
              <div className="flex gap-5">
                <MapPin size={22} className="text-[#E51B23] shrink-0 mt-1" />
                <div>
                  <p className="text-xs uppercase tracking-[0.25em] text-zinc-500 mb-2">Location</p>
                  <p className="text-zinc-100">{contactInfo.address}</p>
                </div>
              </div>
              <div className="flex gap-5">
                <Phone size={22} className="text-[#E51B23] shrink-0 mt-1" />
                <div>
                  <p className="text-xs uppercase tracking-[0.25em] text-zinc-500 mb-2">Phone</p>
                  <a href={`tel:${contactInfo.phone}`} data-testid="contact-phone-link" className="text-zinc-100 hover:text-[#E51B23] transition-colors">{contactInfo.phone}</a>
                </div>
              </div>
              <div className="flex gap-5">
                <Mail size={22} className="text-[#E51B23] shrink-0 mt-1" />
                <div>
                  <p className="text-xs uppercase tracking-[0.25em] text-zinc-500 mb-2">Email</p>
                  <a href={`mailto:${contactInfo.email}`} data-testid="contact-email-link" className="text-zinc-100 hover:text-[#E51B23] transition-colors break-all">{contactInfo.email}</a>
                </div>
              </div>
              <div className="flex gap-5">
                <Instagram size={22} className="text-[#E51B23] shrink-0 mt-1" />
                <div>
                  <p className="text-xs uppercase tracking-[0.25em] text-zinc-500 mb-2">Instagram</p>
                  <a href="https://www.instagram.com/okanaganstreetcustoms/" target="_blank" rel="noopener noreferrer" data-testid="contact-instagram-link" className="text-zinc-100 hover:text-[#E51B23] transition-colors">@okanaganstreetcustoms</a>
                </div>
              </div>
              <div className="flex gap-5">
                <Clock size={22} className="text-[#E51B23] shrink-0 mt-1" />
                <div>
                  <p className="text-xs uppercase tracking-[0.25em] text-zinc-500 mb-2">Hours</p>
                  <p className="text-zinc-100">{contactInfo.hours}</p>
                </div>
              </div>
              <div className="border-t border-zinc-800 pt-8 flex flex-wrap gap-4">
                <Link to="/book" data-testid="contact-book-cta" className="osc-btn-primary">Book Appointment <ArrowRight size={15} /></Link>
                <Link to="/quote" data-testid="contact-quote-cta" className="osc-btn-outline">Free Estimate</Link>
              </div>
            </div>
          </FadeUp>
          <FadeUp delay={0.15} className="lg:col-span-7">
            <div className="border border-zinc-800 overflow-hidden h-[420px] lg:h-full min-h-[420px]" data-testid="contact-map">
              <iframe
                title="Okanagan Street Customs location"
                src="https://maps.google.com/maps?q=2323%20Hunter%20Rd%2C%20Kelowna%2C%20BC&t=&z=14&ie=UTF8&iwloc=&output=embed"
                className="w-full h-full grayscale invert-[0.9] contrast-[0.9]"
                loading="lazy"
              />
            </div>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
